export type TableCloseoutAction = "CONFIRM_CLEANING" | "RELEASE_TABLE";

export interface TableCloseoutCommand {
  kind: "TABLE_CLOSEOUT";
  action: TableCloseoutAction;
  tableId: string;
  tableNumber: string;
  expectedTableRevision: number;
  expectedSessionId: string;
  expectedAggregateVersion: number;
  idempotencyKey: string;
}

export type TableCloseoutRecoveryState = "CLAIMED" | "OUTCOME_UNKNOWN";

export interface TableCloseoutRecovery {
  ownerIdentityKey: string;
  command: TableCloseoutCommand;
  state: TableCloseoutRecoveryState;
}

export type TableCloseoutRecoveryView =
  | { status: "NONE" }
  | { status: "OWNED"; recovery: TableCloseoutRecovery }
  | { status: "OTHER_OPERATOR" }
  | { status: "RECOVERY_STORAGE_UNAVAILABLE"; pendingOutcomeMayExist: boolean };

interface StorageAccess {
  browser: boolean;
  storage: Storage | null;
}

export const TABLE_CLOSEOUT_RECOVERY_STORAGE_KEY = "billiard-erp:table-closeout-recovery:v1";
const RETIRED_CLEAR_TABLE_RECOVERY_STORAGE_KEYS = [
  "billiard-erp:clear-table-recovery:v1",
  "billiard-erp:clear-table-recovery:v2",
] as const;

const definiteNoEffectCodes = new Set([
  "AUTH_REQUIRED",
  "AUTHORIZATION_DEFAULT_DENY",
  "CSRF_REJECTED",
  "IDEMPOTENCY_COLLISION",
  "IDEMPOTENCY_KEY_REUSED",
  "MOBILE_SESSION_ACTIVATION_REQUIRED",
  "NATIVE_APP_VERSION_INVALID",
  "NATIVE_APP_VERSION_MISMATCH",
  "NATIVE_APP_VERSION_REQUIRED",
  "NATIVE_SECURE_SESSION_UNAVAILABLE",
  "NATIVE_SERVER_UPDATE_REQUIRED",
  "NATIVE_UPDATE_REQUIRED",
  "OFFLINE_AUTHORITY_UNAVAILABLE",
  "ONLINE_SERVICE_REQUIRED",
  "ORIGIN_REJECTED",
  "FRONTDESK_OPERATING_FACT_CONFLICT",
  "ORDER_AGGREGATE_VERSION_CONFLICT",
  "TABLE_CLEANING_ALREADY_COMPLETED",
  "TABLE_CLEANING_REQUIRED",
  "TABLE_NOT_FOUND",
  "TABLE_NOT_AWAITING_CLEANING",
  "TABLE_SESSION_CHANGED",
  "TABLE_VERSION_CONFLICT",
]);

let pendingRecovery: TableCloseoutRecovery | null = null;
let dispatchingRecovery: TableCloseoutRecovery | null = null;
let hydrated = false;
let recoveryUnavailable = false;
let pendingOutcomeMayExist = false;

export function tableCloseoutIdentityKey(operator: {
  tenantId: string;
  storeId: string;
  id: string;
}): string {
  return JSON.stringify([operator.tenantId, operator.storeId, operator.id]);
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return value !== null && typeof value === "object" && !Array.isArray(value);
}

function hasExactKeys(record: Record<string, unknown>, keys: readonly string[]): boolean {
  return (
    Object.keys(record).length === keys.length && keys.every((key) => Object.hasOwn(record, key))
  );
}

function isBoundedString(value: unknown, minimum: number, maximum: number): value is string {
  return typeof value === "string" && [...value].length >= minimum && [...value].length <= maximum;
}

function isIdentityKey(value: unknown): value is string {
  if (!isBoundedString(value, 1, 1_024)) return false;
  try {
    const identity = JSON.parse(value) as unknown;
    return (
      Array.isArray(identity) &&
      identity.length === 3 &&
      identity.every((part) => isBoundedString(part, 1, 256))
    );
  } catch {
    return false;
  }
}

function isTableCloseoutCommand(value: unknown): value is TableCloseoutCommand {
  return (
    isRecord(value) &&
    hasExactKeys(value, [
      "kind",
      "action",
      "tableId",
      "tableNumber",
      "expectedTableRevision",
      "expectedSessionId",
      "expectedAggregateVersion",
      "idempotencyKey",
    ]) &&
    value.kind === "TABLE_CLOSEOUT" &&
    (value.action === "CONFIRM_CLEANING" || value.action === "RELEASE_TABLE") &&
    isBoundedString(value.tableId, 1, 256) &&
    isBoundedString(value.tableNumber, 1, 64) &&
    value.tableId === value.tableId.trim() &&
    value.tableNumber === value.tableNumber.trim() &&
    Number.isSafeInteger(value.expectedTableRevision) &&
    (value.expectedTableRevision as number) >= 0 &&
    isBoundedString(value.expectedSessionId, 1, 128) &&
    /^[A-Za-z0-9][A-Za-z0-9._:-]*$/u.test(value.expectedSessionId) &&
    Number.isSafeInteger(value.expectedAggregateVersion) &&
    (value.expectedAggregateVersion as number) >= 1 &&
    isBoundedString(value.idempotencyKey, 8, 128) &&
    value.idempotencyKey === value.idempotencyKey.trim()
  );
}

function immutableRecovery(
  ownerIdentityKey: string,
  command: TableCloseoutCommand,
  state: TableCloseoutRecoveryState,
): TableCloseoutRecovery {
  return Object.freeze({
    ownerIdentityKey,
    command: Object.freeze(structuredClone(command)),
    state,
  });
}

function storedRecovery(value: unknown): TableCloseoutRecovery | null {
  if (
    !isRecord(value) ||
    !hasExactKeys(value, ["version", "recovery"]) ||
    value.version !== 1 ||
    !isRecord(value.recovery) ||
    !hasExactKeys(value.recovery, ["ownerIdentityKey", "command", "state"]) ||
    !isIdentityKey(value.recovery.ownerIdentityKey) ||
    !isTableCloseoutCommand(value.recovery.command) ||
    (value.recovery.state !== "CLAIMED" && value.recovery.state !== "OUTCOME_UNKNOWN")
  ) {
    return null;
  }
  return immutableRecovery(
    value.recovery.ownerIdentityKey,
    value.recovery.command,
    value.recovery.state,
  );
}

function recoveryEquals(left: TableCloseoutRecovery, right: TableCloseoutRecovery): boolean {
  return (
    left.ownerIdentityKey === right.ownerIdentityKey &&
    left.state === right.state &&
    JSON.stringify(left.command) === JSON.stringify(right.command)
  );
}

function sessionStorageAccess(): StorageAccess {
  if (typeof window === "undefined") return { browser: false, storage: null };
  try {
    return {
      browser: true,
      storage: typeof sessionStorage === "undefined" ? null : sessionStorage,
    };
  } catch {
    return { browser: true, storage: null };
  }
}

function readStoredRecovery(storage: Storage): TableCloseoutRecovery | null {
  const encoded = storage.getItem(TABLE_CLOSEOUT_RECOVERY_STORAGE_KEY);
  return encoded === null ? null : storedRecovery(JSON.parse(encoded) as unknown);
}

function saveStoredRecovery(recovery: TableCloseoutRecovery): boolean {
  const access = sessionStorageAccess();
  if (!access.browser) return true;
  if (!access.storage) return false;
  try {
    access.storage.setItem(
      TABLE_CLOSEOUT_RECOVERY_STORAGE_KEY,
      JSON.stringify({ version: 1, recovery }),
    );
    const stored = readStoredRecovery(access.storage);
    return stored !== null && recoveryEquals(stored, recovery);
  } catch {
    return false;
  }
}

function verifyStoredRecovery(recovery: TableCloseoutRecovery): boolean {
  const access = sessionStorageAccess();
  if (!access.browser) return true;
  if (!access.storage) return false;
  try {
    const stored = readStoredRecovery(access.storage);
    return stored !== null && recoveryEquals(stored, recovery);
  } catch {
    return false;
  }
}

function hydrateStoredRecovery(): void {
  if (hydrated) return;
  hydrated = true;
  const access = sessionStorageAccess();
  if (!access.browser) return;
  if (!access.storage) {
    recoveryUnavailable = true;
    return;
  }
  try {
    const encoded = access.storage.getItem(TABLE_CLOSEOUT_RECOVERY_STORAGE_KEY);
    if (encoded === null) {
      if (
        RETIRED_CLEAR_TABLE_RECOVERY_STORAGE_KEYS.some(
          (key) => access.storage?.getItem(key) !== null,
        )
      ) {
        pendingOutcomeMayExist = true;
        recoveryUnavailable = true;
      }
      return;
    }
    pendingOutcomeMayExist = true;
    const stored = storedRecovery(JSON.parse(encoded) as unknown);
    if (!stored) {
      recoveryUnavailable = true;
      return;
    }
    const recovered =
      stored.state === "CLAIMED"
        ? immutableRecovery(stored.ownerIdentityKey, stored.command, "OUTCOME_UNKNOWN")
        : stored;
    if (stored.state === "CLAIMED" && !saveStoredRecovery(recovered)) {
      recoveryUnavailable = true;
      return;
    }
    pendingRecovery = recovered;
  } catch {
    recoveryUnavailable = true;
  }
}

export function readTableCloseoutRecovery(identityKey: string): TableCloseoutRecoveryView {
  hydrateStoredRecovery();
  if (recoveryUnavailable || !isIdentityKey(identityKey)) {
    return { status: "RECOVERY_STORAGE_UNAVAILABLE", pendingOutcomeMayExist };
  }
  if (!pendingRecovery) return { status: "NONE" };
  if (pendingRecovery.ownerIdentityKey !== identityKey) return { status: "OTHER_OPERATOR" };
  return { status: "OWNED", recovery: pendingRecovery };
}

export function claimTableCloseoutRecovery(
  identityKey: string,
  command: TableCloseoutCommand,
): TableCloseoutRecoveryView {
  const current = readTableCloseoutRecovery(identityKey);
  if (current.status !== "NONE") return current;
  if (!isIdentityKey(identityKey) || !isTableCloseoutCommand(command)) {
    recoveryUnavailable = true;
    return { status: "RECOVERY_STORAGE_UNAVAILABLE", pendingOutcomeMayExist };
  }
  const recovery = immutableRecovery(identityKey, command, "CLAIMED");
  if (!saveStoredRecovery(recovery)) {
    recoveryUnavailable = true;
    return { status: "RECOVERY_STORAGE_UNAVAILABLE", pendingOutcomeMayExist };
  }
  pendingRecovery = recovery;
  pendingOutcomeMayExist = true;
  return { status: "OWNED", recovery };
}

function verifyRecoveryForDispatch(recovery: TableCloseoutRecovery, identityKey: string): boolean {
  hydrateStoredRecovery();
  if (
    recoveryUnavailable ||
    pendingRecovery !== recovery ||
    recovery.ownerIdentityKey !== identityKey ||
    !verifyStoredRecovery(recovery)
  ) {
    recoveryUnavailable = true;
    return false;
  }
  return true;
}

export function beginTableCloseoutDispatch(
  recovery: TableCloseoutRecovery,
  identityKey: string,
): boolean {
  if (dispatchingRecovery || !verifyRecoveryForDispatch(recovery, identityKey)) return false;
  dispatchingRecovery = recovery;
  return true;
}

export function finishTableCloseoutDispatch(
  recovery: TableCloseoutRecovery,
  identityKey: string,
): boolean {
  if (dispatchingRecovery !== recovery || recovery.ownerIdentityKey !== identityKey) return false;
  dispatchingRecovery = null;
  return true;
}

export function markTableCloseoutOutcomeUnknown(
  recovery: TableCloseoutRecovery,
  identityKey: string,
): boolean {
  if (!verifyRecoveryForDispatch(recovery, identityKey)) return false;
  const unknown =
    recovery.state === "OUTCOME_UNKNOWN"
      ? recovery
      : immutableRecovery(identityKey, recovery.command, "OUTCOME_UNKNOWN");
  if (!saveStoredRecovery(unknown)) {
    recoveryUnavailable = true;
    return false;
  }
  pendingRecovery = unknown;
  pendingOutcomeMayExist = true;
  return true;
}

export function clearTableCloseoutRecovery(
  recovery: TableCloseoutRecovery,
  identityKey: string,
): boolean {
  if (
    recoveryUnavailable ||
    pendingRecovery !== recovery ||
    recovery.ownerIdentityKey !== identityKey
  ) {
    return false;
  }
  const access = sessionStorageAccess();
  if (access.browser) {
    if (!access.storage) {
      recoveryUnavailable = true;
      return false;
    }
    try {
      const stored = readStoredRecovery(access.storage);
      if (!stored || !recoveryEquals(stored, recovery)) {
        recoveryUnavailable = true;
        return false;
      }
      access.storage.removeItem(TABLE_CLOSEOUT_RECOVERY_STORAGE_KEY);
      if (access.storage.getItem(TABLE_CLOSEOUT_RECOVERY_STORAGE_KEY) !== null) {
        recoveryUnavailable = true;
        return false;
      }
    } catch {
      recoveryUnavailable = true;
      return false;
    }
  }
  pendingRecovery = null;
  pendingOutcomeMayExist = false;
  return true;
}

export function tableCloseoutOutcomeIsUnknown(error: unknown): boolean {
  return !(
    isRecord(error) &&
    typeof error.code === "string" &&
    definiteNoEffectCodes.has(error.code)
  );
}

export function resetTableCloseoutRecoveryForTests(): void {
  pendingRecovery = null;
  dispatchingRecovery = null;
  hydrated = false;
  recoveryUnavailable = false;
  pendingOutcomeMayExist = false;
  const access = sessionStorageAccess();
  try {
    access.storage?.removeItem(TABLE_CLOSEOUT_RECOVERY_STORAGE_KEY);
    for (const key of RETIRED_CLEAR_TABLE_RECOVERY_STORAGE_KEYS) {
      access.storage?.removeItem(key);
    }
  } catch {
    // Test cleanup also resets the in-memory fail-closed state.
  }
}

export function resetTableCloseoutRecoveryMemoryForTests(): void {
  pendingRecovery = null;
  dispatchingRecovery = null;
  hydrated = false;
  recoveryUnavailable = false;
  pendingOutcomeMayExist = false;
}
